import React, { useState, useEffect } from 'react';
import { Steps, Card, Button, Space, Progress } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setCurrentStep as setCurrentStepAction, markStepCompleted } from '../../redux/reducers/navigationSlice';
import useAbandonIncident from '../../hooks/useAbandonIncident';
import AuthService from '../../services/auth';
import Api from '../../services/api';
import Insights from '../../services/insightsService';
import { transformIncidentData } from '../../core/helpers/transform';
import { UpdateIncidentRequest } from '../../types/api.types';
import { 
  CarOutlined, 
  UserOutlined, 
  CameraOutlined, 
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  DownOutlined,
  UpOutlined,
  ToolOutlined,
  DashboardOutlined
} from '@ant-design/icons';
import VinScanStep from './steps/VinScanStep';
import VehicleConfirmationStep from './steps/VehicleConfirmationStep';
import OperatorSelectionStep from './steps/OperatorSelectionStep';
import PhotoCaptureStep from './steps/PhotoCaptureStep';
import DamageLocationStep from './steps/DamageLocationStep';
import DamagedPartsStep from './steps/DamagedPartsStep';
import VehicleStatusStep from './steps/VehicleStatusStep';
import IncidentDetailsStep from './steps/IncidentDetailsStep';
import ReviewSubmitStep from './steps/ReviewSubmitStep';
import './IncidentCreationScreen.css';

const { Step } = Steps;

// Step indices (9 steps total):
// 0: Vehicle ID, 1: Confirm, 2: Operator, 3: Photos,
// 4: Damage Location, 5: Damaged Parts, 6: Vehicle Status,
// 7: Incident Details, 8: Review & Submit
const DAMAGED_PARTS_STEP_INDEX = 5;
const INCIDENT_DETAILS_STEP_INDEX = 7;

const steps = [
  {
    title: 'Vehicle ID',
    icon: <CarOutlined />,
    component: VinScanStep,
  },
  {
    title: 'Confirm',
    icon: <CarOutlined />,
    component: VehicleConfirmationStep,
  },
  {
    title: 'Operator',
    icon: <UserOutlined />,
    component: OperatorSelectionStep,
  },
  {
    title: 'Photos',
    icon: <CameraOutlined />,
    component: PhotoCaptureStep,
  },
  {
    title: 'Damage Location',
    icon: <WarningOutlined />,
    component: DamageLocationStep,
  },
  {
    title: 'Damaged Parts',
    icon: <ToolOutlined />,
    component: DamagedPartsStep,
  },
  {
    title: 'Vehicle Status',
    icon: <DashboardOutlined />,
    component: VehicleStatusStep,
  },
  {
    title: 'Details',
    icon: <FileTextOutlined />,
    component: IncidentDetailsStep,
  },
  {
    title: 'Review',
    icon: <CheckCircleOutlined />,
    component: ReviewSubmitStep,
  },
];

export const IncidentCreationScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { onAbandonPress } = useAbandonIncident();
  
  // Get current step from Redux state
  const reduxCurrentStep = useAppSelector(state => state.navigation.currentStep);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const completedSteps = useAppSelector(state => state.navigation.completedSteps);
  const damageLocations = useAppSelector(state => state.damageLocations);
  const userRoles = useAppSelector(state => state.auth.userData?.roles ?? []);
  const isOperator = AuthService.isOperator(userRoles);
  
  const vehicle = useAppSelector(state => state.vehicle);
  const operatorInfo = useAppSelector(state => state.operatorInfo);
  const incidentDetails = useAppSelector(state => state.incidentDetails);
  const damagedParts = useAppSelector(state => state.damagedParts);
  const vehiclePhotos = useAppSelector(state => state.vehiclePhotos);
  const auth = useAppSelector(state => state.auth);

  const VEHICLE_STATUS_STEP_INDEX = 6;

  const saveIntermediateProgress = async () => {
    if (!incidentDetails.incidentId) return;
    try {
      const incidentData = transformIncidentData({
        damageLocations,
        damagedParts,
        incidentDetails,
        vehicle,
        vehiclePhotos,
        operatorInfo,
        auth
      }, true) as UpdateIncidentRequest;
      incidentData.Finalize = false;
      await Api.updateIncident(incidentData);
    } catch (error) {
      Insights.trackException(error instanceof Error ? error : new Error(
        typeof error === "object" && error !== null
          ? JSON.stringify(error)
          : String(error)
      ));
    }
  };
  
  const [currentStep, setCurrentStepLocal] = useState(reduxCurrentStep);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isStepsExpanded, setIsStepsExpanded] = useState(false);
  const [isDevelopmentMode, setIsDevelopmentMode] = useState(process.env.NODE_ENV === 'development');
  
  // Sync local state with Redux state
  useEffect(() => {
    setCurrentStepLocal(reduxCurrentStep);
  }, [reduxCurrentStep]);
  
  // Update function to sync with Redux
  const setCurrentStep = (step: number) => {
    setCurrentStepLocal(step);
    dispatch(setCurrentStepAction(step));
  };

  const visibleSteps = isOperator
    ? steps
    : steps.filter((_, index) => index !== INCIDENT_DETAILS_STEP_INDEX);

  const visibleCurrentStep = isOperator
    ? currentStep
    : currentStep > INCIDENT_DETAILS_STEP_INDEX
      ? currentStep - 1
      : currentStep;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      dispatch(markStepCompleted(currentStep));

      let nextStep = currentStep + 1;
      if (nextStep === DAMAGED_PARTS_STEP_INDEX) {
        const { hasFrontEndDamage, hasRearEndDamage, hasSideDamage } = damageLocations;
        if (!hasFrontEndDamage && !hasRearEndDamage && !hasSideDamage) {
          dispatch(markStepCompleted(DAMAGED_PARTS_STEP_INDEX));
          nextStep = DAMAGED_PARTS_STEP_INDEX + 1;
        }
      }

      if (nextStep === INCIDENT_DETAILS_STEP_INDEX && !isOperator) {
        dispatch(markStepCompleted(INCIDENT_DETAILS_STEP_INDEX));
        nextStep = INCIDENT_DETAILS_STEP_INDEX + 1;
      }

      if (currentStep === VEHICLE_STATUS_STEP_INDEX) {
        saveIntermediateProgress();
      }

      setCurrentStep(nextStep);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      let prevStep = currentStep - 1;

      if (prevStep === INCIDENT_DETAILS_STEP_INDEX && !isOperator) {
        prevStep = INCIDENT_DETAILS_STEP_INDEX - 1;
      }

      if (prevStep === DAMAGED_PARTS_STEP_INDEX) {
        const { hasFrontEndDamage, hasRearEndDamage, hasSideDamage } = damageLocations;
        if (!hasFrontEndDamage && !hasRearEndDamage && !hasSideDamage) {
          prevStep = DAMAGED_PARTS_STEP_INDEX - 1;
        }
      }

      setCurrentStep(prevStep);
    }
  };

  const handleComplete = () => {
    navigate('/home');
  };

  const handleSkipToStep = (stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
    }
  };

  const handleSkipForward = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSkipBackward = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const CurrentStepComponent = steps[currentStep].component;

  // Combined Mobile Navigation Component
  const MobileCombinedNav = () => {
    const progressPercent = Math.round(((visibleCurrentStep + 1) / visibleSteps.length) * 100);
    
    return (
      <div className="mobile-combined-nav">
        {/* Progress Bar Section */}
        <div className="mobile-nav-header">
          <div className="mobile-step-info">
            <span className="mobile-step-counter">Step {visibleCurrentStep + 1} of {visibleSteps.length}: </span>
            <span className="mobile-step-title">{visibleSteps[visibleCurrentStep].title}</span>
          </div>
          <Button
            type="text"
            size="small"
            icon={isStepsExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={() => setIsStepsExpanded(!isStepsExpanded)}
            className="mobile-nav-dropdown"
          />
        </div>
        
        <Progress 
          percent={progressPercent} 
          showInfo={false}
          strokeColor="#FF9902"
          className="mobile-progress-bar"
        />
        
        {/* Expandable Steps List */}
        {isStepsExpanded && (
          <div className="mobile-steps-dropdown">
            <div className="mobile-steps-list">
              {visibleSteps.map((step, index) => {
                const realIndex = steps.indexOf(step);
                return (
                <div 
                  key={index}
                  className={`mobile-step-item ${realIndex === currentStep ? 'active' : ''} ${realIndex < currentStep ? 'completed' : ''}`}
                  onClick={() => {
                    handleSkipToStep(realIndex);
                    setIsStepsExpanded(false);
                  }}
                >
                  <div className="mobile-step-icon">
                    {step.icon}
                  </div>
                  <div className="mobile-step-content">
                    <span className="mobile-step-name">{step.title}</span>
                    <span className="mobile-step-number">Step {index + 1}</span>
                  </div>
                  <div className="mobile-step-status">
                    {realIndex < currentStep && <CheckCircleOutlined className="mobile-step-check" />}
                    {realIndex === currentStep && <span className="mobile-step-current">Current</span>}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Development Navigation Toolbar
  const DevelopmentToolbar = () => {
    if (!isDevelopmentMode) return null;

    return (
      <div className="dev-toolbar">
        <div className="dev-toolbar-header">
          <strong>🚧 DEV TOOLS</strong>
          <Button 
            type="link" 
            size="small" 
            onClick={() => setIsDevelopmentMode(!isDevelopmentMode)}
          >
            Hide
          </Button>
        </div>
        <div className="dev-toolbar-content">
          <Space wrap>
            <Button size="small" onClick={handleSkipBackward} disabled={currentStep === 0}>
              ← Previous
            </Button>
            <Button size="small" onClick={handleSkipForward} disabled={currentStep === steps.length - 1}>
              Next →
            </Button>
            <span className="dev-step-selector">Jump to:</span>
            {steps.map((step, index) => (
              <Button 
                key={index}
                size="small"
                type={index === currentStep ? 'primary' : 'default'}
                onClick={() => handleSkipToStep(index)}
              >
                {index + 1}
              </Button>
            ))}
          </Space>
        </div>
      </div>
    );
  };

  return (
    <div className="incident-creation-container">
      <Card className="incident-creation-card">
        {/* <div className="incident-header">
          <h1>Create Incident Report</h1>
        </div> */}

        {isMobile ? (
          <MobileCombinedNav />
        ) : (
          <Steps 
            current={visibleCurrentStep} 
            className="incident-steps"
            direction="horizontal"
            size="default"
          >
            {visibleSteps.map((step, index) => (
              <Step key={index} title={step.title} icon={step.icon} />
            ))}
          </Steps>
        )}

        <DevelopmentToolbar />

        <div className="step-content">
          <CurrentStepComponent 
            onNext={handleNext}
            onPrevious={handlePrevious}
            onComplete={handleComplete}
            isFirstStep={currentStep === 0}
            isLastStep={currentStep === steps.length - 1}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <Button type="text" onClick={onAbandonPress} style={{ color: '#FF9902', fontWeight: 'bold' }}>
            Abandon Incident
          </Button>
        </div>
      </Card>

    </div>
  );
};